/**
 * Pure locale-routing helpers (issue #221 PR #2 — locale URL prefix).
 *
 * Single source of truth for the `/:locale/...` URL contract that the route
 * nesting (App.tsx), the URL⇄locale sync (useLocaleViewModel), the locale-aware
 * navigation hooks, and the hreflang/canonical head emitter (LocaleHead) all
 * build on.
 *
 * Conventions (see TODO-221 TD-1):
 *   - URL path segments are LOWERCASE BCP-47 tags (`/sv-se/products`).
 *   - The canonical locale form (`sv-SE`, region upper-cased) is what i18next,
 *     localStorage and the hreflang attribute value carry — the catalog holds it.
 *
 * Dependency-free + SSR-safe (no `window`), so they are unit-testable and usable
 * in any render path.
 */

import type { LocaleCatalogEntry } from "../viewmodels/useLocaleViewModel";

/** Lowercase URL path segment for a BCP-47 locale (`sv-SE` → `sv-se`). */
export function localeToPathSegment(locale: string): string {
	return (locale ?? "").toLowerCase();
}

/**
 * True for an internal absolute app path (`/products`) that should be
 * locale-prefixed; false for external/protocol-relative URLs (`https://…`,
 * `//cdn…`), anchors (`#x`), and `mailto:`/`tel:` links — which must pass through
 * untouched. Used to guard config-driven hrefs (e.g. hero CTA actions).
 */
export function isInternalPath(href: string | undefined | null): boolean {
	if (!href) return false;
	return href.startsWith("/") && !href.startsWith("//");
}

/**
 * Match a URL path segment (case-insensitive) against the supported catalog,
 * returning the catalog's canonical BCP-47 locale (e.g. `sv-SE`) or `null` when
 * the segment is not a supported locale.
 */
export function findSupportedLocale(
	segment: string | undefined | null,
	supported: LocaleCatalogEntry[],
): string | null {
	if (!segment) return null;
	const lower = segment.toLowerCase();
	const match = supported.find((entry) => entry.locale.toLowerCase() === lower);
	return match ? match.locale : null;
}

export interface SplitLocale {
	/** Canonical locale when the path led with a supported segment, else null. */
	locale: string | null;
	/** The path with the locale segment removed (always leading-slash, no trailing). */
	rest: string;
}

/**
 * Split a pathname into its leading locale segment (if supported) and the rest.
 *   `/sv-se/products` → { locale: "sv-SE", rest: "/products" }
 *   `/sv-se`          → { locale: "sv-SE", rest: "/" }
 *   `/products`       → { locale: null,    rest: "/products" }
 */
export function splitLocaleFromPath(
	pathname: string,
	supported: LocaleCatalogEntry[],
): SplitLocale {
	const clean = pathname && pathname.length > 0 ? pathname : "/";
	const segments = clean.replace(/^\/+/, "").split("/");
	const first = segments[0] ?? "";
	const locale = findSupportedLocale(first, supported);
	if (!locale) {
		const rest = clean.startsWith("/") ? clean : `/${clean}`;
		return { locale: null, rest: normaliseRest(rest) };
	}
	const rest = `/${segments.slice(1).join("/")}`;
	return { locale, rest: normaliseRest(rest) };
}

/** Collapse to a single leading slash and drop a trailing slash (except root). */
function normaliseRest(path: string): string {
	const withLead = path.startsWith("/") ? path : `/${path}`;
	if (withLead === "/") return "/";
	return withLead.replace(/\/+$/, "") || "/";
}

/**
 * Prefix `path` with the lowercase locale segment, preserving query + hash so
 * deep links survive. Idempotent: a path that already leads with THIS locale's
 * segment is returned unchanged (it does not swap a different leading locale —
 * compose with {@link splitLocaleFromPath} when swapping).
 *
 *   withLocale("sv-SE", "/products?page=2") → "/sv-se/products?page=2"
 *   withLocale("sv-SE", "/")                → "/sv-se"
 *   withLocale("sv-SE", "/sv-se/products")  → "/sv-se/products"  (idempotent)
 */
export function withLocale(locale: string, path: string): string {
	const seg = localeToPathSegment(locale);
	const raw = path && path.length > 0 ? path : "/";
	const parts = /^([^?#]*)([?#].*)?$/.exec(raw);
	const pathOnly = parts?.[1] || "/";
	const suffix = parts?.[2] ?? "";
	const normalized = pathOnly.startsWith("/") ? pathOnly : `/${pathOnly}`;
	const firstSeg = normalized.replace(/^\/+/, "").split("/")[0]?.toLowerCase();
	if (firstSeg === seg) {
		// Already localised with this exact locale — idempotent no-op.
		return normalized + suffix;
	}
	const tail = normalized === "/" ? "" : normalized;
	return `/${seg}${tail}${suffix}`;
}

/**
 * Swap whatever locale a pathname leads with for `nextLocale`, preserving the
 * logical path + query + hash. Used when the user picks a new locale so the URL
 * updates in place. `/sv-se/products?x=1` + en-US → `/en-us/products?x=1`.
 */
export function swapLocaleInPath(
	nextLocale: string,
	pathname: string,
	supported: LocaleCatalogEntry[],
	search = "",
	hash = "",
): string {
	const { rest } = splitLocaleFromPath(pathname, supported);
	return `${withLocale(nextLocale, rest)}${search}${hash}`;
}
